// Print utilities for PRIXDEAL admin pages
import { escapeHtml, safeWriteToWindow } from './sanitize';

/**
 * Opens a print-friendly view in a new window with the provided content
 * @param {string} title - The title of the printed document
 * @param {string} content - HTML content to print
 * @returns {boolean} - Returns true if the print window was opened successfully
 */
export const printContent = (title, content) => {
    // Try to open a new window for printing
    const printWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes,menubar=yes,toolbar=yes');
    
    if (!printWindow || printWindow.closed || typeof printWindow.closed === 'undefined') {
        // Popup blocked — fallback: print in an iframe
        try {
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.top = '-10000px';
            iframe.style.left = '-10000px';
            iframe.style.width = '900px';
            iframe.style.height = '700px';
            document.body.appendChild(iframe);
            
            const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
            safeWriteToWindow(iframe.contentWindow, buildPrintHTML(title, content, false));
            
            iframe.onload = function() {
                setTimeout(function() {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                    setTimeout(function() { document.body.removeChild(iframe); }, 1000);
                }, 800);
            };
            return true;
        } catch (e) {
            alert("Impossible d'ouvrir la fenetre d'impression.\n\nAutorisez les popups pour ce site :\n1. Cliquez sur l'icone cadenas dans la barre d'adresse\n2. Autorisez les fenetres contextuelles\n3. Actualisez la page et reessayez");
            return false;
        }
    }

    const html = buildPrintHTML(title, content, true);
    safeWriteToWindow(printWindow, html);
    printWindow.focus();
    return true;
};

function buildPrintHTML(title, content, showActions) {

    const styles = `
        <style>
            * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }
            
            body {
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
                padding: 20px;
                color: #1A4D2E;
                font-size: 12px;
                line-height: 1.5;
            }
            
            .print-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-bottom: 2px solid #1A4D2E;
                padding-bottom: 15px;
                margin-bottom: 20px;
            }
            
            .print-logo {
                font-size: 24px;
                font-weight: 700;
                color: #1A4D2E;
            }
            
            .print-logo img {
                height: 40px;
                width: auto;
            }
            
            .print-logo span {
                color: #22C55E;
            }
            
            .print-date {
                font-size: 11px;
                color: #666;
            }
            
            .print-title {
                font-size: 18px;
                font-weight: 600;
                margin-bottom: 15px;
                color: #1A4D2E;
            }
            
            .print-subtitle {
                font-size: 14px;
                font-weight: 500;
                margin: 15px 0 10px;
                color: #1A4D2E;
                border-bottom: 1px solid #e5e7eb;
                padding-bottom: 5px;
            }
            
            table {
                width: 100%;
                border-collapse: collapse;
                margin: 10px 0;
            }
            
            th, td {
                padding: 8px 10px;
                text-align: left;
                border-bottom: 1px solid #e5e7eb;
            }
            
            th {
                background-color: #f9fafb;
                font-weight: 600;
                font-size: 11px;
                text-transform: uppercase;
                color: #666;
            }
            
            tr:nth-child(even) {
                background-color: #fafaf9;
            }
            
            .badge {
                display: inline-block;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 10px;
                font-weight: 500;
            }
            
            .badge-active {
                background-color: #dcfce7;
                color: #166534;
            }
            
            .badge-inactive {
                background-color: #fee2e2;
                color: #991b1b;
            }
            
            .badge-category {
                background-color: #e0f2fe;
                color: #0369a1;
            }
            
            .badge-status {
                background-color: #fef3c7;
                color: #92400e;
            }
            
            .client-card {
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                padding: 15px;
                margin-bottom: 15px;
                page-break-inside: avoid;
            }
            
            .client-card-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 10px;
            }
            
            .client-card-title {
                font-size: 16px;
                font-weight: 600;
                color: #1A4D2E;
            }
            
            .client-card-code {
                font-family: monospace;
                background-color: #f3f4f6;
                padding: 2px 6px;
                border-radius: 4px;
                font-size: 11px;
            }
            
            .client-card-info {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 8px;
                font-size: 11px;
            }
            
            .client-card-info dt {
                color: #666;
            }
            
            .client-card-info dd {
                color: #1A4D2E;
                font-weight: 500;
            }
            
            .order-detail {
                border: 1px solid #e5e7eb;
                border-radius: 8px;
                margin-bottom: 20px;
                page-break-inside: avoid;
            }
            
            .order-header {
                background-color: #1A4D2E;
                color: white;
                padding: 12px 15px;
                border-radius: 8px 8px 0 0;
            }
            
            .order-header h3 {
                font-size: 14px;
                font-weight: 600;
            }
            
            .order-header p {
                font-size: 11px;
                opacity: 0.8;
            }
            
            .order-body {
                padding: 15px;
            }
            
            .order-total {
                text-align: right;
                font-size: 16px;
                font-weight: 700;
                color: #1A4D2E;
                padding: 10px 15px;
                background-color: #f0fdf4;
                border-radius: 0 0 8px 8px;
            }
            
            .print-footer {
                margin-top: 30px;
                padding-top: 15px;
                border-top: 1px solid #e5e7eb;
                font-size: 10px;
                color: #666;
                text-align: center;
            }
            
            .no-print {
                display: none !important;
            }
            
            .filter-info {
                background-color: #f5f5f0;
                padding: 8px 12px;
                border-radius: 6px;
                font-size: 11px;
                margin-bottom: 15px;
            }
            
            @media print {
                body {
                    padding: 0;
                }
                
                .print-header {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    background: white;
                    padding: 10px 20px;
                }
                
                .content {
                    margin-top: 60px;
                }
            }
        </style>
    `;

    const html = `
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="utf-8">
            <title>${title} - PRIXDEAL</title>
            ${styles}
        </head>
        <body>
            ${showActions ? `<div class="print-actions no-print" style="text-align: center; margin-bottom: 20px; padding: 15px; background: #f0f9f0; border-radius: 8px;">
                <p style="margin-bottom: 10px; color: #1A4D2E;">Cliquez sur le bouton ci-dessous ou utilisez Ctrl+P pour imprimer</p>
                <button onclick="window.print()" style="padding: 12px 40px; font-size: 16px; background: #1A4D2E; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: bold;">
                    IMPRIMER
                </button>
            </div>` : ''}
            <div class="print-header">
                <div class="print-logo"><img src="https://static.prod-images.emergentagent.com/jobs/024b891f-fc27-4019-b26c-2b8150881fe7/images/3f33a4765d25945394125711d8735c8fcbc83fbe600546da05c827445ed740eb.png" alt="PRIXDEAL" onerror="this.style.display='none';this.parentNode.textContent='PRIXDEAL'" /></div>
                <div class="print-date">Imprime le ${new Date().toLocaleDateString('fr-FR')} a ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            <div class="content">
                ${content}
            </div>
            <div class="print-footer">
                <p>PRIXDEAL - CENTRA 307, 94586 RUNGIS CEDEX | 1 RUE DE LA CORDERIE, 94550 CHEVILLY-LARUE</p>
                <p>Contact: 06 62 66 76 39 | asaber@prixdeal.fr</p>
            </div>
            ${showActions ? `<script>
                // Robust auto-print: wait for all images then print
                function tryPrint() {
                    var images = document.querySelectorAll('img');
                    var loaded = 0;
                    var total = images.length;
                    if (total === 0) { setTimeout(function(){ window.print(); }, 300); return; }
                    images.forEach(function(img) {
                        if (img.complete) { loaded++; }
                        else {
                            img.onload = img.onerror = function() { loaded++; if (loaded >= total) setTimeout(function(){ window.print(); }, 300); };
                        }
                    });
                    if (loaded >= total) setTimeout(function(){ window.print(); }, 300);
                }
                if (document.readyState === 'complete') tryPrint();
                else window.addEventListener('load', tryPrint);
            </script>` : ''}
        </body>
        </html>
    `;

    return html;
};

/**
 * Formats price for display
 */
export const formatPriceForPrint = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(price || 0);
};

/**
 * Formats date for display
 */
export const formatDateForPrint = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

/**
 * Generate HTML for clients list
 */
export const generateClientsListHTML = (clients, filters = {}) => {
    const filterInfo = [];
    if (filters.category) filterInfo.push(`Catégorie: ${filters.category}`);
    if (filters.status) filterInfo.push(`Statut: ${filters.status}`);
    if (filters.search) filterInfo.push(`Recherche: "${filters.search}"`);

    let html = `<h1 class="print-title">Liste des Clients (${clients.length})</h1>`;
    
    if (filterInfo.length > 0) {
        html += `<div class="filter-info">Filtres appliqués: ${filterInfo.join(' | ')}</div>`;
    }

    html += `
        <table>
            <thead>
                <tr>
                    <th>Code</th>
                    <th>Entreprise</th>
                    <th>Contact</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Catégorie</th>
                    <th>Statut</th>
                </tr>
            </thead>
            <tbody>
                ${clients.map(client => `
                    <tr>
                        <td><code>${client.client_code || '-'}</code></td>
                        <td><strong>${client.company_name}</strong>${client.siret ? `<br><small>SIRET: ${client.siret}</small>` : ''}</td>
                        <td>${client.contact_name}</td>
                        <td>${client.email}</td>
                        <td>${client.phone}</td>
                        <td><span class="badge badge-category">${client.client_category || 'Autre'}</span></td>
                        <td><span class="badge ${client.is_active ? 'badge-active' : 'badge-inactive'}">${client.is_active ? 'Actif' : 'Inactif'}</span></td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    
    return html;
};

/**
 * Generate HTML for single client card
 */
export const generateClientCardHTML = (client) => {
    return `
        <h1 class="print-title">Fiche Client</h1>
        <div class="client-card">
            <div class="client-card-header">
                <div>
                    <div class="client-card-title">${client.company_name}</div>
                    ${client.siret ? `<div style="font-size: 11px; color: #666;">SIRET: ${client.siret}</div>` : ''}
                </div>
                ${client.client_code ? `<span class="client-card-code">${client.client_code}</span>` : ''}
            </div>
            <dl class="client-card-info">
                <dt>Contact</dt>
                <dd>${client.contact_name}</dd>
                
                <dt>Email</dt>
                <dd>${client.email}</dd>
                
                <dt>Téléphone</dt>
                <dd>${client.phone}</dd>
                
                <dt>Catégorie</dt>
                <dd><span class="badge badge-category">${client.client_category || 'Autre'}</span></dd>
                
                <dt>Adresse</dt>
                <dd>${client.address}</dd>
                
                <dt>Statut</dt>
                <dd><span class="badge ${client.is_active ? 'badge-active' : 'badge-inactive'}">${client.is_active ? 'Actif' : 'Inactif'}</span></dd>
                
                <dt>Date de création</dt>
                <dd>${formatDateForPrint(client.created_at)}</dd>
            </dl>
        </div>
    `;
};

/**
 * Generate HTML for products list
 */
export const generateProductsListHTML = (products, categories, filters = {}) => {
    const getCategoryName = (categoryId) => {
        const cat = categories.find(c => c.id === categoryId);
        return cat ? cat.name : categoryId || '-';
    };

    const filterInfo = [];
    if (filters.category) filterInfo.push(`Famille: ${escapeHtml(getCategoryName(filters.category))}`);
    if (filters.status) filterInfo.push(`Statut: ${escapeHtml(filters.status)}`);
    if (filters.search) filterInfo.push(`Recherche: "${escapeHtml(filters.search)}"`);

    let html = `<h1 class="print-title">Liste des Produits (${products.length})</h1>`;
    
    if (filterInfo.length > 0) {
        html += `<div class="filter-info">Filtres appliques: ${filterInfo.join(' | ')}</div>`;
    }

    html += `
        <table>
            <thead>
                <tr>
                    <th>Code</th>
                    <th>Nom</th>
                    <th>Famille</th>
                    <th>Prix</th>
                    <th>Unité</th>
                    <th>Stock</th>
                    <th>Statut</th>
                </tr>
            </thead>
            <tbody>
                ${products.map(product => `
                    <tr>
                        <td><code>${escapeHtml(product.product_code || '-')}</code></td>
                        <td><strong>${escapeHtml(product.name)}</strong>${product.origin ? `<br><small>Origine: ${escapeHtml(product.origin)}</small>` : ''}</td>
                        <td>${escapeHtml(getCategoryName(product.category_id))}</td>
                        <td><strong>${formatPriceForPrint(product.price)}</strong></td>
                        <td>${product.unit}</td>
                        <td>${product.stock || 0}</td>
                        <td>
                            <span class="badge ${product.is_active ? 'badge-active' : 'badge-inactive'}">${product.is_active ? 'Actif' : 'Masqué'}</span>
                            ${product.is_weekly_offer ? '<span class="badge badge-status" style="margin-left: 4px;">Promo</span>' : ''}
                        </td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    
    return html;
};

/**
 * Generate HTML for orders list
 */
export const generateOrdersListHTML = (orders, filters = {}) => {
    const statusLabels = {
        'pending': 'En attente',
        'confirmed': 'Confirmée',
        'preparing': 'En préparation',
        'shipped': 'Expédiée',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };

    const filterInfo = [];
    if (filters.status) filterInfo.push(`Statut: ${escapeHtml(statusLabels[filters.status] || filters.status)}`);
    if (filters.search) filterInfo.push(`Recherche: "${escapeHtml(filters.search)}"`);

    let html = `<h1 class="print-title">Liste des Commandes (${orders.length})</h1>`;
    
    if (filterInfo.length > 0) {
        html += `<div class="filter-info">Filtres appliques: ${filterInfo.join(' | ')}</div>`;
    }

    html += `
        <table>
            <thead>
                <tr>
                    <th>N° Commande</th>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Articles</th>
                    <th>Total</th>
                    <th>Statut</th>
                </tr>
            </thead>
            <tbody>
                ${orders.map(order => `
                    <tr>
                        <td><strong>${escapeHtml(order.order_number)}</strong></td>
                        <td>${escapeHtml(order.company_name)}</td>
                        <td>${formatDateForPrint(order.created_at)}</td>
                        <td>${order.items?.length || 0} article(s)</td>
                        <td><strong>${formatPriceForPrint(order.total)}</strong></td>
                        <td><span class="badge badge-status">${statusLabels[order.status] || order.status}</span></td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    
    return html;
};

/**
 * Generate HTML for single order detail
 */
export const generateOrderDetailHTML = (order) => {
    const statusLabels = {
        'pending': 'En attente',
        'confirmed': 'Confirmée',
        'preparing': 'En préparation',
        'shipped': 'Expédiée',
        'delivered': 'Livrée',
        'cancelled': 'Annulée'
    };

    return `
        <h1 class="print-title">Détail de la Commande</h1>
        <div class="order-detail">
            <div class="order-header">
                <h3>Commande ${escapeHtml(order.order_number)}</h3>
                <p>${escapeHtml(order.company_name)}${order.client_code ? ` | Code client : ${escapeHtml(order.client_code)}` : ''} | ${formatDateForPrint(order.created_at)} | Statut: ${statusLabels[order.status] || escapeHtml(order.status)}</p>
            </div>
            <div class="order-body">
                <table>
                    <thead>
                        <tr>
                            <th>Produit</th>
                            <th>Quantité</th>
                            <th>Prix unitaire</th>
                            <th>Sous-total</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${(order.items || []).map(item => `
                            <tr>
                                <td><strong>${escapeHtml(item.product_name)}</strong>${item.package_weight > 0 ? ` <small style="color:#92400E">(${item.package_weight} ${item.unit === 'pièce' ? 'pièces' : escapeHtml(item.unit)}/colis)</small>` : ''}</td>
                                <td>${item.quantity} colis${item.package_weight > 0 ? ` x ${item.package_weight} ${item.unit === 'pièce' ? 'pièces' : escapeHtml(item.unit)}` : ''}</td>
                                <td>${formatPriceForPrint(item.price)}${item.package_weight > 0 ? '/' + escapeHtml(item.unit) : ''}</td>
                                <td><strong>${formatPriceForPrint(item.subtotal)}</strong></td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                
                ${order.delivery_address ? `
                    <h3 class="print-subtitle">Adresse de livraison</h3>
                    <p>${escapeHtml(order.delivery_address)}</p>
                ` : ''}
                
                ${order.notes ? `
                    <h3 class="print-subtitle">Notes</h3>
                    <p>${escapeHtml(order.notes)}</p>
                ` : ''}
            </div>
            <div class="order-total">
                Total HT: ${formatPriceForPrint(order.total)}
            </div>
        </div>
    `;
};


/**
 * Generate HTML for stock preparation report
 */
export const generateStockReportHTML = (stockItems, ordersCount) => {
    let html = `<h1 class="print-title">Preparation de Commande</h1>`;
    html += `<div class="filter-info">${ordersCount} commande(s) selectionnee(s) | ${stockItems.length} produit(s) a preparer</div>`;
    html += `
        <table>
            <thead>
                <tr>
                    <th>Produit</th>
                    <th style="text-align:center">Qte totale</th>
                    <th style="text-align:center">Unite</th>
                    <th style="text-align:center">Poids total (kg)</th>
                    <th style="text-align:center">Nb commandes</th>
                    <th style="text-align:center;width:40px">OK</th>
                </tr>
            </thead>
            <tbody>
                ${stockItems.map(item => `
                    <tr>
                        <td><strong>${escapeHtml(item.product_name)}</strong></td>
                        <td style="text-align:center;font-size:14px;font-weight:700">${item.total_quantity}</td>
                        <td style="text-align:center">${escapeHtml(item.unit)}</td>
                        <td style="text-align:center">${item.total_weight_kg > 0 ? item.total_weight_kg.toFixed(1) + ' kg' : '-'}</td>
                        <td style="text-align:center">${item.orders_count}</td>
                        <td style="text-align:center;border:2px solid #ccc;width:40px;height:20px"></td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
    `;
    return html;
};

/**
 * Generate HTML for delivery route report
 */
export const generateDeliveryReportHTML = (deliveries, ordersCount) => {
    let html = `<h1 class="print-title">Rapport de Tournee Livraison</h1>`;
    html += `<div class="filter-info">${ordersCount} livraison(s) a effectuer</div>`;
    html += `
        <table>
            <thead>
                <tr>
                    <th style="width:30px">#</th>
                    <th>Commande</th>
                    <th>Client</th>
                    <th>Adresse de livraison</th>
                    <th>Tel</th>
                    <th style="text-align:center">Articles</th>
                    <th style="text-align:right">Total</th>
                    <th>Notes</th>
                    <th style="text-align:center;width:40px">OK</th>
                </tr>
            </thead>
            <tbody>
                ${deliveries.map((d, i) => `
                    <tr>
                        <td style="font-weight:700">${i + 1}</td>
                        <td><strong>${escapeHtml(d.order_number)}</strong></td>
                        <td>${escapeHtml(d.company_name)}${d.client_code ? `<br><small>${escapeHtml(d.client_code)}</small>` : ''}</td>
                        <td>${d.address ? escapeHtml(d.address) : '<em style="color:#999">Non renseignee</em>'}</td>
                        <td>${escapeHtml(d.phone || '-')}</td>
                        <td style="text-align:center">${d.items_count}</td>
                        <td style="text-align:right"><strong>${formatPriceForPrint(d.total)}</strong></td>
                        <td style="font-size:10px">${escapeHtml(d.notes || '')}</td>
                        <td style="text-align:center;border:2px solid #ccc;width:40px;height:20px"></td>
                    </tr>
                `).join('')}
            </tbody>
        </table>
        <div style="margin-top:20px;text-align:right;font-size:14px;font-weight:700">
            Total general: ${formatPriceForPrint(deliveries.reduce((s, d) => s + (d.total || 0), 0))}
        </div>
    `;
    return html;
};
